import React, { useState, useRef, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLocation } from 'react-router-dom';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import { useNavigate, Link } from 'react-router-dom';
import { cleanOrgName } from '../utils/formatters';

import {
  Buildings,
  CaretDown,
  Check,
  Crown,
  GearSix,
  Code,
  ListDashes,
  PencilSimple,
  Plus,
  SignOut,
  Trash,
  Lightning
} from '@phosphor-icons/react';
import { fetchWithOrg } from '../utils/api';

const TopBar = ({ onMenuClick }) => {
  const location = useLocation();
  const { plan: tier } = usePlan();
  const { activeOrganization, organizations, setActiveOrganization, createOrganization, updateOrganization, deleteOrganization } = useOrganization();
  const { logout } = useAuth();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [createOrgError, setCreateOrgError] = useState('');
  const [editOrgError, setEditOrgError] = useState('');
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(null);
  const profileRef = useRef(null);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch credit balance from org-level pricing endpoint (same source as sidebar)
  useEffect(() => {
    const orgId = activeOrganization?.organization_id;
    if (!orgId) return;
    const fetchCredits = async () => {
      try {
        const res = await fetchWithOrg(`${BACKEND_URL}/api/pricing/balance`, {
          headers: { 'x-org-id': orgId }
        });
        if (res.ok) {
          const data = await res.json();
          setCredits(data);
        }
      } catch {}
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [activeOrganization?.organization_id]);

  const handleLogout = async (e) => {
    // Prevent event bubbling that might close dropdown before action completes
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Use AuthContext logout - handles API call, localStorage clearing, forceAuthRemount
    // This is the same logout used by Sidebar (bottom-left logout)
    await logout();
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setCreateOrgError('');
    
    if (!newOrgName.trim()) {
      setCreateOrgError('Organization name is required');
      return;
    }
    
    const result = await createOrganization(newOrgName.trim());
    
    if (result.success) {
      setShowCreateOrgModal(false);
      setNewOrgName('');
      setCreateOrgError('');
    } else {
      setCreateOrgError(result.error || 'Failed to create organization');
    }
  };

  const handleEditOrganization = async (e) => {
    e.preventDefault();
    setEditOrgError('');
    
    if (!editOrgName.trim()) {
      setEditOrgError('Organization name is required');
      return;
    }
    
    const result = await updateOrganization(selectedOrg.organization_id, editOrgName.trim());
    
    if (result.success) {
      setShowEditOrgModal(false);
      setSelectedOrg(null);
      setEditOrgName('');
      setEditOrgError('');
    } else {
      setEditOrgError(result.error || 'Failed to update organization');
    }
  };

  const handleDeleteOrganization = async () => {
    const result = await deleteOrganization(selectedOrg.organization_id);
    
    if (result.success) {
      setShowDeleteConfirmModal(false);
      setSelectedOrg(null);
    } else {
      alert(result.error || 'Failed to delete organization');
    }
  };

  const openEditModal = (org, e) => {
    e.stopPropagation();
    setSelectedOrg(org);
    setEditOrgName(org.name);
    setShowProfileMenu(false);
    setShowEditOrgModal(true);
  };

  const openDeleteModal = (org, e) => {
    e.stopPropagation();
    setSelectedOrg(org);
    setShowProfileMenu(false);
    setShowDeleteConfirmModal(true);
  };

  // Get page title from route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Home';
    if (path.includes('/automations/new')) return 'New Worker';
    if (path.includes('/automations')) return 'Workers';
    if (path.includes('/inbox')) return 'Activity';
    if (path.includes('/apps')) return 'Connected Tools';
    if (path.includes('/team-members')) return 'Team';
    if (path.includes('/assessments')) return 'Assessments';
    if (path.includes('/processes')) return 'Processes';
    if (path.includes('/job-descriptions')) return 'Job Descriptions';
    if (path.includes('/opportunities')) return 'Opportunities';
    if (path.includes('/org-chart')) return 'Org Charts';
    if (path.includes('/external-agent')) return 'External Agents';
    if (path.includes('/ai-workforce')) return 'Workers';
    if (path.includes('/analytics/voice')) return 'Voice Analytics';
    if (path.includes('/analytics')) return 'Analytics';
    if (path.includes('/unified-agent-builder')) return 'Agent Builder';
    if (path.includes('/my-automations')) return 'My Automations';
    if (path.includes('/approval-dashboard')) return 'Approvals';
    if (path.includes('/app-store')) return 'App Store';
    if (path.includes('/run-health')) return 'Run Health';
    if (path.includes('/reports')) return 'Reports';
    if (path.includes('/process-templates')) return 'Templates';
    if (path.includes('/settings/admin')) return 'Org Admin';
    if (path.includes('/settings/voice')) return 'Voice Settings';
    if (path.includes('/settings')) return 'Settings';
    if (path.includes('/account')) return 'Account';
    return 'M & M';
  };

  const getTierBadge = () => {
    if (!tier) return null;
    
    const badges = {
      'basic': { color: 'from-[#718096] to-[#5C6778]', label: 'Basic', icon: null },
      'starter': { color: 'from-[#718096] to-[#5C6778]', label: 'Basic', icon: null },
      'free': { color: 'from-[#718096] to-[#5C6778]', label: 'Basic', icon: null },
      'professional': { color: 'from-[#10B981] to-[#059669]', label: 'Professional', icon: null },
      'builder': { color: 'from-[#10B981] to-[#059669]', label: 'Professional', icon: null },
      'teams': { color: 'from-[#6366F1] to-[#8B5CF6]', label: 'Teams', icon: null },
      'pro': { color: 'from-[#6366F1] to-[#8B5CF6]', label: 'Teams', icon: null },
      'partner-pro': { color: 'from-[#14F195] to-[#10B981]', label: 'Partner Pro', icon: Crown },
      'partner_pro': { color: 'from-[#14F195] to-[#10B981]', label: 'Partner Pro', icon: Crown }
    };
    
    const badge = badges[tier] || badges.basic;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r ${badge.color} text-[#0B0F14] text-[11px] font-semibold`}>
        {Icon && <Icon size={11} />}
        {badge.label}
      </span>
    );
  };

  return (
    <>
    <header className="h-14 bg-[#141B24] border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between px-6">
      {/* Left: ListDashes button (mobile) + Page Title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
        >
          <ListDashes size={24} className="text-white" />
        </button>
        <h1 className="text-base font-semibold text-white">{getPageTitle()}</h1>
      </div>

      {/* Right: Credits + Tier Badge + Profile */}
      <div className="flex items-center gap-3">
        {/* Credits — visible globally */}
        {credits && (
          <div
            data-testid="topbar-credits"
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.08)] cursor-default"
            title={`${(credits.balance ?? 0).toLocaleString()} / ${(credits.monthly_allowance ?? 0).toLocaleString()} credits`}
          >
            <Lightning size={13} weight="fill" className="text-[#00E5C0]" />
            <span className="text-[11px] font-semibold text-[#00E5C0]">
              {(credits.balance ?? 0).toLocaleString()}
            </span>
          </div>
        )}
        {getTierBadge()}

        {/* Profile ListDashes */}
        <div className="relative z-[100]" ref={profileRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--surface-3)] transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00C9A7] to-[#00E5C0] flex items-center justify-center">
              <span className="text-[#0B0F14] text-sm font-bold">
                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <CaretDown size={16} className="text-white hidden md:block" />
          </button>

          {showProfileMenu && (
            <>
              <div 
                className="fixed inset-0 z-[90]"
                onClick={() => setShowProfileMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-64 bg-[var(--surface-2)] rounded-[10px] border border-[var(--border-default)] shadow-[var(--shadow-xl)] overflow-hidden z-[100]">
                <div className="p-4 border-b border-[var(--border-subtle)]">
                  <p className="text-white font-semibold truncate">{cleanOrgName(user?.name) || user?.email || 'User'}</p>
                  <p className="text-[#A0AEC0] text-sm truncate">{cleanOrgName(activeOrganization?.name) || 'No organization'}</p>
                </div>
                
                {/* Organization Switcher - Multi-tenant */}
                {(tier === 'partner_pro' || tier === 'partner-pro') && (
                  <div className="border-b border-[rgba(255,255,255,0.1)]">
                    <div className="px-4 py-3">
                      <p className="text-xs text-[#A0AEC0] uppercase tracking-wide mb-2">Organizations</p>
                      
                      {/* List of organizations */}
                      <div className="space-y-1 mb-2">
                        {organizations.map((org) => (
                          <div
                            key={org.organization_id}
                            className={`group w-full flex items-center gap-2 px-3 py-2 text-sm rounded transition-all ${
                              org.organization_id === activeOrganization?.organization_id
                                ? 'bg-gradient-to-r from-[#00C9A7] to-[#00E5C0] text-[#0B0F14] font-semibold'
                                : 'text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white'
                            }`}
                          >
                            <button
                              onClick={() => {
                                if (org.organization_id !== activeOrganization?.organization_id) {
                                  setActiveOrganization(org.organization_id);
                                }
                              }}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            >
                              <Buildings size={16} className="flex-shrink-0" />
                              <span className="truncate block" title={cleanOrgName(org.name)}>{cleanOrgName(org.name)}</span>
                            </button>
                            
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {org.organization_id === activeOrganization?.organization_id && (
                                <Check size={16} className="flex-shrink-0" />
                              )}
                              
                              {/* PencilSimple Icon - Only for admins */}
                              {isAdmin && (
                                <button
                                  onClick={(e) => openEditModal(org, e)}
                                  className="p-1 hover:bg-[rgba(255,255,255,0.2)] rounded transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                                  title="Edit organization name"
                                >
                                  <PencilSimple size={14} />
                                </button>
                              )}
                              
                              {/* Trash Icon - Only for admins and non-primary organizations */}
                              {isAdmin && !org.is_primary && (
                                <button
                                  onClick={(e) => openDeleteModal(org, e)}
                                  className="p-1 hover:bg-[rgba(239,68,68,0.2)] rounded transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                                  title="Delete organization"
                                >
                                  <Trash size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Create New Organization button - Only for admins */}
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            setShowCreateOrgModal(true);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#14F195] hover:bg-[rgba(20,241,149,0.1)] transition-all rounded border border-[rgba(20,241,149,0.3)]"
                        >
                          <Plus size={16} />
                          <span>Create Organization</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/account');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all"
                >
                  <GearSix size={18} />
                  <span className="text-sm">Account</span>
                </button>
                {/* Developer Portal — hidden, may resurface in future */}
                {/* {isAdmin && (
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/developer');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all"
                    data-testid="developer-portal-link"
                  >
                    <Code size={18} />
                    <span className="text-sm">Developer Portal</span>
                  </button>
                )} */}
                {/* Billing - Only for admins */}
                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/billing');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all"
                  >
                    <Crown size={18} />
                    <span className="text-sm">Billing</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#EF4444] hover:bg-[rgba(255,255,255,0.1)] transition-all border-t border-[rgba(255,255,255,0.1)]"
                >
                  <SignOut size={18} />
                  <span className="text-sm">Sign Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>

    {/* Create Organization Modal */}
    {showCreateOrgModal && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 w-full max-w-md">
          <h3 className="text-xl font-bold text-white mb-4">Create New Organization</h3>
          
          <form onSubmit={handleCreateOrganization}>
            <div className="mb-4">
              <label className="block text-sm text-[#A0AEC0] mb-2">Organization Name</label>
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Enter organization name"
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0] transition-colors"
                autoFocus
              />
            </div>
            
            {createOrgError && (
              <div className="mb-4 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg">
                <p className="text-sm text-[#EF4444]">{createOrgError}</p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateOrgModal(false);
                  setNewOrgName('');
                  setCreateOrgError('');
                }}
                className="flex-1 px-4 py-3 rounded-lg border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-[#00C9A7] to-[#00E5C0] text-[#0B0F14] font-semibold hover:opacity-90 transition-opacity"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* PencilSimple Organization Modal */}
    {showEditOrgModal && selectedOrg && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 w-full max-w-md">
          <h3 className="text-xl font-bold text-white mb-4">Edit Organization</h3>
          
          <form onSubmit={handleEditOrganization}>
            <div className="mb-4">
              <label className="block text-sm text-[#A0AEC0] mb-2">Organization Name</label>
              <input
                type="text"
                value={editOrgName}
                onChange={(e) => setEditOrgName(e.target.value)}
                placeholder="Enter organization name"
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0] transition-colors"
                autoFocus
              />
            </div>
            
            {editOrgError && (
              <div className="mb-4 p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg">
                <p className="text-sm text-[#EF4444]">{editOrgError}</p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditOrgModal(false);
                  setSelectedOrg(null);
                  setEditOrgName('');
                  setEditOrgError('');
                }}
                className="flex-1 px-4 py-3 rounded-lg border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-[#00C9A7] to-[#00E5C0] text-[#0B0F14] font-semibold hover:opacity-90 transition-opacity"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Trash Confirmation Modal */}
    {showDeleteConfirmModal && selectedOrg && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 w-full max-w-md">
          <h3 className="text-xl font-bold text-white mb-4">Delete Organization</h3>
          
          <div className="mb-6">
            <p className="text-[#A0AEC0] mb-2">Are you sure you want to delete this organization?</p>
            <p className="text-white font-semibold">{selectedOrg.name}</p>
            <p className="text-sm text-[#EF4444] mt-3">This action cannot be undone. All data associated with this organization will be permanently deleted.</p>
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setSelectedOrg(null);
              }}
              className="flex-1 px-4 py-3 rounded-lg border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteOrganization}
              className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-[#EF4444] to-[#DC2626] text-white hover:opacity-90 transition-opacity"
            >
              Trash
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default TopBar;
